import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import MDBox from "components/MDBox";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getDateInFormat } from "utils/dateUtil";
import * as AppConstants from "AppConstants";
import { validateHolidayObject } from "utils/holidayValidationUtil";
import { addHolidayToBackend, updateHolidayToBackend } from "app/services/holiday.service";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

const obj = {
  holidayDate: getDateInFormat(new Date()),
  name: "",
  description: "",
  holidayType: "PUBLIC",
};

const AddHolidayDialog = ({ open, setOpen, refreshActivitiesDataHandler, editObject, calendarId }) => {
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);

  useEffect(() => {
    setMessageOpen(false);
    setMessage("");
    if (!editObject) setData({ ...obj });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editObject]);

  useEffect(() => {
    if (editObject) {
      setData({
        holidayDate: editObject.holidayDate,
        name: editObject.name,
        description: editObject.description || "",
        holidayType: editObject.holidayType || "PUBLIC",
      });
    }
  }, [editObject]);

  const saveHolidayInBackend = async () => {
    const payload = trimTextFields(data);
    if (validateHolidayObject(payload) && !editObject) await addHolidayInvoker(payload);
    else if (validateHolidayObject(payload) && editObject) await updateHolidayInvoker(payload);
    else {
      setErrorAction("One or more required fields for Holiday are missing or having invalid value");
    }
  };

  const addHolidayInvoker = async (holidayObject) => {
    await addHolidayToBackend(calendarId, holidayObject)
      .then(() => {
        setSuccessAction("Holiday added successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  const updateHolidayInvoker = async (holidayObject) => {
    await updateHolidayToBackend(calendarId, editObject.id, holidayObject)
      .then(() => {
        setSuccessAction("Holiday updated successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update Holiday` : `Add New Holiday`}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Holiday Date"
              value={data.holidayDate ? dayjs(data.holidayDate) : null}
              onChange={(newDate) => {
                setData({ ...data, holidayDate: getDateInFormat(newDate) });
              }}
              format={AppConstants.DATE_FORMAT_DISPLAY}
            />
          </LocalizationProvider>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="holiday-name"
            label="Name"
            fullWidth
            variant="outlined"
            value={data && data.name}
            onBlur={trimOnBlur(setData, "name")}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="holiday-description"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            minRows={2}
            value={data && data.description}
            onBlur={trimOnBlur(setData, "description")}
            onChange={(e) => setData({ ...data, description: e.target.value })}
            helperText="Optional"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="holiday-type"
            label="Type"
            select
            fullWidth
            variant="outlined"
            value={data.holidayType || "PUBLIC"}
            onChange={(e) => setData({ ...data, holidayType: e.target.value })}
            helperText="Optional holidays must be availed by each member (quota applies)"
          >
            <MenuItem value="PUBLIC">Public</MenuItem>
            <MenuItem value="OPTIONAL">Optional</MenuItem>
          </TextField>
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveHolidayInBackend} variant="contained">
          {editObject ? `Update` : `Add`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddHolidayDialog);
